import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import TopMenu from '../../contexts/TopMenu';
import Modal from '../global/Modal';
import { API_BASE_URL } from '../../config';
import '../../styles/ProductDetails.css';

function ImportToShopifyModal({ isOpen, onClose, email, productId }) {
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchCollections();
    }
  }, [isOpen]);

  const fetchCollections = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/brands/collections/all`);
      const data = await response.json();
      
      if (data.status === 200) {
        setCollections(data.data.collections);
      } else {
        console.error('Unexpected response status:', data.status);
      }
    } catch (error) {
      console.error('Error fetching collections:', error);
      setError('Failed to fetch collections');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      var payload = {
        email,
        product_id: productId,
        collection: selectedCollection
      };
      console.log(payload);
      const response = await fetch(`${API_BASE_URL}/brands/mine/shopify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      
      if (response.ok) {
        setSuccess('Product imported successfully');
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setError(data.message || 'Failed to import product');
      }
    } catch (error) {
      console.error('Error importing product:', error);
      setError('Failed to import product');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Import to Shopify"
      size="small"
    >
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="collection">Collection:</label>
          <select
            id="collection"
            value={selectedCollection}
            onChange={(e) => setSelectedCollection(e.target.value)}
            required
          >
            <option value="">Select a collection</option>
            {collections.map((collection) => (
              <option key={collection.handle} value={collection.handle}>
                {collection.title}
              </option>
            ))}
          </select>
        </div>
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}
        <div className="modal-actions">
          <button type="button" onClick={onClose} className="cancel-button">
            Cancel
          </button>
          <button 
            type="submit" 
            disabled={loading} 
            className="action-button"
          >
            {loading ? 'Importing...' : 'Import Product'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function ImportScreen({ email, onLogout }) {
  const location = useLocation();
  const { product, product_id } = location.state || {};
  const [selectedVariant, setSelectedVariant] = useState(product?.variants?.[0]);
  const [selectedImage, setSelectedImage] = useState(product?.images?.[0]);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  if (!product) {
    return <div>Product not found</div>;
  }

  return (
    <div className="dashboard-root">
      <TopMenu pageTitle="Import Data" email={email} onLogout={onLogout} />
      <div className="dashboard-container">
        <div className="product-details-root">
          <div className="product-details-container">
            <div className="product-details">
              <div className="product-details-content">
                <div className="product-details-left">
                  <div className="product-images">
                    <div className="main-image">
                      <img 
                        src={selectedImage?.url || product.images?.[0]?.url || '/placeholder-product.png'} 
                        alt={product.title}
                      />
                    </div>
                    <div className="image-thumbnails">
                      {product.images?.map((image) => (
                        <div 
                          key={image.id} 
                          className={`thumbnail ${selectedImage?.id === image.id ? 'selected' : ''}`}
                          onClick={() => setSelectedImage(image)}
                        >
                          <img src={image.url} alt={product.title} />
                        </div>
                      ))}
                    </div>
                    <div className="import-section" style={{ marginTop: '20px', textAlign: 'center' }}>
                      <button
                        onClick={() => setIsImportModalOpen(true)}
                        className="action-button"
                        style={{
                          backgroundColor: '#004d1a',
                          width: '100%',
                          padding: '12px'
                        }}
                      >
                        Import to Shopify
                      </button>
                    </div>
                  </div>
                </div>

                <div className="product-details-right">
                  <div className="product-info-section">
                    <h2>Product Information</h2>
                    <div className="info-group">
                      <label>Title</label>
                      <div className="info-value">{product.title}</div>
                    </div>
                    <div className="info-group">
                      <label>Description</label>
                      <div className="info-value" dangerouslySetInnerHTML={{ __html: product.description_html }} />
                    </div>
                  </div>

                  <div className="product-variants-section">
                    <h2>Variants</h2>
                    <div className="variants-grid">
                      {product.variants?.map((variant) => (
                        <div 
                          key={variant.id}
                          className={`variant-card ${selectedVariant?.id === variant.id ? 'selected' : ''}`}
                          onClick={() => setSelectedVariant(variant)}
                        >
                          <h3>{variant.title}</h3>
                          <div className="variant-details">
                            <p>Price: ₹{variant.price}</p>
                            {variant.compare_at_price && (
                              <p className="compare-price">Compare at: ₹{variant.compare_at_price}</p>
                            )}
                            <p>SKU: {variant.sku || 'N/A'}</p>
                            <p>Stock: {variant.inventory_quantity}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="product-metadata-section">
                    <h2>Additional Details</h2>
                    <div className="metadata-grid">
                      <div className="info-group">
                        <label>Product Type</label>
                        <div className="info-value">{product.product_type}</div>
                      </div>
                      <div className="info-group">
                        <label>Vendor</label>
                        <div className="info-value">{product.vendor}</div>
                      </div>
                      <div className="info-group">
                        <label>Tags</label>
                        <div className="info-value tags-list">
                          {product.tags?.map((tag) => (
                            <span key={tag} className="tag">{tag}</span>
                          ))}
                        </div>
                      </div>
                      {product.metafields?.custom?.about_fabric && (
                        <div className="info-group">
                          <label>Fabric</label>
                          <div className="info-value">{product.metafields.custom.about_fabric}</div>
                        </div>
                      )}
                      {product.metafields?.custom?.washing_instructions && (
                        <div className="info-group">
                          <label>Care Instructions</label>
                          <div className="info-value">{product.metafields.custom.washing_instructions}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <ImportToShopifyModal
          isOpen={isImportModalOpen}
          onClose={() => setIsImportModalOpen(false)}
          email={email}
          productId={product_id}
        />
      </div>
    </div>
  );
}

export default ImportScreen; 